import React, { createContext, useState, useEffect, useMemo } from 'react'
import { useMatchBreakpoints } from '@donaswap/uikit'
import { ChainId } from '@donaswap/sdk'
import { useExchangeChartManager } from 'state/user/hooks'
import { useActiveChainId } from 'hooks/useActiveChainId'
import useMoonscan, { Moonscan } from 'hooks/useMoonscan'
import useBubblemaps, { Bubblemaps } from 'hooks/useBubblemaps'

export const SwapFeaturesContext = createContext<{
  isHotTokenSupported: boolean
  isChartSupported: boolean
  isStableSupported: boolean
  isAccessTokenSupported: boolean
  isChartExpanded: boolean
  isChartDisplayed: boolean
  setIsChartExpanded: React.Dispatch<React.SetStateAction<boolean>>
  setIsChartDisplayed: React.Dispatch<React.SetStateAction<boolean>>
  moonscan: Moonscan
  bubblemaps: Bubblemaps
}>({
  isHotTokenSupported: false,
  isChartSupported: false,
  isStableSupported: false,
  isAccessTokenSupported: false,
  isChartExpanded: false,
  isChartDisplayed: false,
  setIsChartExpanded: null,
  setIsChartDisplayed: null,
  moonscan: null,
  bubblemaps: null,
})

const CHART_SUPPORT_CHAIN_IDS = [
  ChainId.FIRECHAIN,
  ChainId.RINIA,
  ChainId.FIRECHAIN_ZKEVM,
  ChainId.FIRECHAIN_ZKEVM_GHOSTRIDER,
  ChainId.ETHEREUM,
  ChainId.BSC,
  ChainId.BSC_TESTNET,
  ChainId.POLYGON,
  ChainId.POLYGON_ZKEVM,
  ChainId.CRO,
  ChainId.AVALANCHE,
  ChainId.FANTOM_OPERA,
  ChainId.CELO,
  ChainId.OPTIMISM,
  ChainId.ARBITRUM_ONE,
  ChainId.SHIBARIUM,
  ChainId.PULSECHAIN,
  ChainId.XEN,
  ChainId.HARMONY,
  ChainId.COINEX,
  ChainId.DOGECHAIN,
  ChainId.ETHEREUM_CLASSIC,
  ChainId.FUSION,
  ChainId.HECO,
  ChainId.KCC,
  ChainId.KLAYTN,
  ChainId.OKXCHAIN,
  ChainId.THETHA,
  ChainId.ULTRON,
  ChainId.MOONBEAM,
  ChainId.MOONRIVER,
  ChainId.AURORA,
  ChainId.BOBA,
  ChainId.GNOSIS,
  ChainId.METIS,
  ChainId.TELOS,
  ChainId.SYSCOIN,
  ChainId.IOTEX,
  ChainId.SHIDEN,
  ChainId.ASTAR,
  ChainId.FUSE,
  ChainId.VELAS,
  ChainId.REI,
  ChainId.KEKCHAIN,
  ChainId.TOMOCHAIN,
  ChainId.THUNDERCORE,
  ChainId.WANCHAIN,
  ChainId.RSK,
  ChainId.ELASTOS,
  ChainId.CONFLUX,
  ChainId.BRISECHAIN,
  ChainId.MUUCHAIN,
  ChainId.CANTO,
  ChainId.OASIS_EMERALD,
  ChainId.VISION,
  ChainId.STEP,
  ChainId.METER,
  ChainId.GODWOKEN,
  ChainId.CALLISTO,
  ChainId.EVMOS,
  ChainId.ENERGY_WEB_CHAIN,
  ChainId.BASE,
  ChainId.KAVA,
  ChainId.CLOVER,
  ChainId.DEFICHAIN,
  ChainId.BRONOS,
  ChainId.FILECOIN,
  ChainId.FLARE,
  ChainId.TARAXA,
  ChainId.ZKSYNC,
  ChainId.LINEA,
  ChainId.BTTCHAIN,
  ChainId.BOBA_AVAX,
  ChainId.BOBA_BNB,
  ChainId.CMP,
  ChainId.CORE,
  ChainId.CUBECHAIN,
  ChainId.DARWINIA_CRAB,
  ChainId.DIODE_PRETNET,
  ChainId.GOCHAIN,
  ChainId.HAQQ,
  ChainId.KARDIACHAIN,
  ChainId.METADIUM,
  ChainId.OP_BNB,
  ChainId.PALM,
  ChainId.POLIS,
  ChainId.SHIBACHAIN,
  ChainId.SMART_BCH,
  ChainId.SONGBIRD_CANARY,
  ChainId.THAICHAIN,
  ChainId.UBIQ,
  ChainId.VECHAIN,
  ChainId.XINFIN,
  ChainId.SCROLL,
  ChainId.XRPL_DEVNET,
  ChainId.ZETACHAIN,
  ChainId.ZYX,
]
const ACCESS_TOKEN_SUPPORT_CHAIN_IDS = [
  ChainId.BSC
]
const STABLE_SUPPORT_CHAIN_IDS = [
  ChainId.BSC_TESTNET,
  ChainId.BSC
]
const HOT_TOKEN_SUPPORT_CHAIN_IDS = [
  ChainId.FIRECHAIN,
  ChainId.ETHEREUM,
  ChainId.BSC,
  ChainId.POLYGON,
  ChainId.POLYGON_ZKEVM,
  ChainId.CRO,
  ChainId.AVALANCHE,
  ChainId.FANTOM_OPERA,
  ChainId.CELO,
  ChainId.OPTIMISM,
  ChainId.ARBITRUM_ONE,
  ChainId.SHIBARIUM,
  ChainId.PULSECHAIN,
  ChainId.XEN,
  ChainId.HARMONY,
  ChainId.COINEX,
  ChainId.DOGECHAIN,
  ChainId.ETHEREUM_CLASSIC,
  ChainId.FUSION,
  ChainId.HECO,
  ChainId.KCC,
  ChainId.KLAYTN,
  ChainId.OKXCHAIN,
  ChainId.THETHA,
  ChainId.ULTRON,
  ChainId.MOONBEAM,
  ChainId.MOONRIVER,
  ChainId.AURORA,
  ChainId.BOBA,
  ChainId.GNOSIS,
  ChainId.METIS,
  ChainId.TELOS,
  ChainId.SYSCOIN,
  ChainId.IOTEX,
  ChainId.SHIDEN,
  ChainId.ASTAR,
  ChainId.FUSE,
  ChainId.VELAS,
  ChainId.REI,
  ChainId.KEKCHAIN,
  ChainId.TOMOCHAIN,
  ChainId.THUNDERCORE,
  ChainId.WANCHAIN,
  ChainId.RSK,
  ChainId.ELASTOS,
  ChainId.CONFLUX,
  ChainId.BRISECHAIN,
  ChainId.MUUCHAIN,
  ChainId.CANTO,
  ChainId.OASIS_EMERALD,
  ChainId.VISION,
  ChainId.STEP,
  ChainId.METER,
  ChainId.GODWOKEN,
  ChainId.CALLISTO,
  ChainId.EVMOS,
  ChainId.ENERGY_WEB_CHAIN,
  ChainId.BASE,
  ChainId.KAVA,
  ChainId.CLOVER,
  ChainId.DEFICHAIN,
  ChainId.BRONOS,
  ChainId.FILECOIN,
  ChainId.FLARE,
  ChainId.TARAXA,
  ChainId.ZKSYNC,
  ChainId.LINEA,
  ChainId.BTTCHAIN,
  ChainId.BOBA_AVAX,
  ChainId.BOBA_BNB,
  ChainId.CMP,
  ChainId.CORE,
  ChainId.CUBECHAIN,
  ChainId.DARWINIA_CRAB,
  ChainId.DIODE_PRETNET,
  ChainId.GOCHAIN,
  ChainId.HAQQ,
  ChainId.KARDIACHAIN,
  ChainId.METADIUM,
  ChainId.OP_BNB,
  ChainId.PALM,
  ChainId.POLIS,
  ChainId.SHIBACHAIN,
  ChainId.SMART_BCH,
  ChainId.SONGBIRD_CANARY,
  ChainId.THAICHAIN,
  ChainId.UBIQ,
  ChainId.VECHAIN,
  ChainId.XINFIN,
  ChainId.SCROLL,
  ChainId.XRPL_DEVNET,
  ChainId.ZETACHAIN,
  ChainId.ZYX
]
// const MOONSCAN_SUPPORT_CHAIN_IDS = [
//   ChainId.BSC, 
//   ChainId.ETHEREUM
// ]
// const BUBBLE_MAPS_SUPPORT_CHAIN_IDS = [
//   ChainId.BSC, 
//   ChainId.ETHEREUM
// ]
export const SwapFeaturesProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isMobile } = useMatchBreakpoints()
  const { chainId } = useActiveChainId()
  const [userChartPreference, setUserChartPreference] = useExchangeChartManager(isMobile)
  const [isChartDisplayed, setIsChartDisplayed] = useState(userChartPreference)
  const [isChartExpanded, setIsChartExpanded] = useState(false)
  const moonscan = useMoonscan()
  const bubblemaps = useBubblemaps()

  const isChartSupported = useMemo(
    () =>
      // avoid layout shift, by default showing
      !chainId || CHART_SUPPORT_CHAIN_IDS.includes(chainId),
    [chainId],
  )

  const isStableSupported = useMemo(() => !chainId || STABLE_SUPPORT_CHAIN_IDS.includes(chainId), [chainId])

  const isAccessTokenSupported = useMemo(() => ACCESS_TOKEN_SUPPORT_CHAIN_IDS.includes(chainId), [chainId])

  const isHotTokenSupported = useMemo(() => HOT_TOKEN_SUPPORT_CHAIN_IDS.includes(chainId), [chainId])
  
  // const isMoonscanSupported = useMemo(() => MOONSCAN_SUPPORT_CHAIN_IDS.includes(chainId), [chainId])
  
  // const isBubblemapsSupported = useMemo(() => BUBBLE_MAPS_SUPPORT_CHAIN_IDS.includes(chainId), [chainId])
  
  useEffect(() => {
    setUserChartPreference(isChartDisplayed)
  }, [isChartDisplayed, setUserChartPreference])

  const value = useMemo(() => {
    return {
      isHotTokenSupported,
      isChartSupported,
      isStableSupported,
      isAccessTokenSupported,
      isChartDisplayed,
      setIsChartDisplayed,
      isChartExpanded,
      setIsChartExpanded,
      moonscan,
      bubblemaps,
    }
  }, [
    isHotTokenSupported,
    isChartSupported,
    isStableSupported,
    isAccessTokenSupported,
    isChartDisplayed,
    setIsChartDisplayed,
    isChartExpanded,
    setIsChartExpanded,
    moonscan,
    moonscan,
  ])

  return <SwapFeaturesContext.Provider value={value}>{children}</SwapFeaturesContext.Provider>
}
